import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import type { SxProps, Theme } from "@mui/material/styles";

/**
 * Vendored + adapted from Mantis Free's `components/MainCard.jsx` (MIT — fetched 2026-08,
 * docs/337-ADMIN-MANTIS-MIGRATION.md). Adaptations from the original:
 *  - converted PropTypes → a typed TS interface;
 *  - dropped `theme.vars.customShadows`/`theme.vars.palette` (MUI's newer CSS-variables theming
 *    feature, which this evaluation build's theme doesn't opt into — see `theme/index.tsx`) in
 *    favor of plain `theme.palette.divider` + MUI's own default `Card` elevation;
 *  - dropped the `codeHighlight`/`modal` variants (unused by any ported page here).
 * Behavior otherwise matches the original: an optional titled header (with a divider under it)
 * over a padded content area — the "every card on the dashboard" building block.
 */
export function MainCard({
  border = true,
  children,
  content = true,
  contentSx,
  divider = true,
  secondary,
  sx,
  title,
  subheader,
}: {
  border?: boolean;
  children: React.ReactNode;
  content?: boolean;
  contentSx?: SxProps<Theme>;
  divider?: boolean;
  secondary?: React.ReactNode;
  sx?: SxProps<Theme>;
  title?: React.ReactNode;
  subheader?: React.ReactNode;
}) {
  return (
    <Card
      elevation={0}
      sx={[
        (theme) => ({
          position: "relative",
          ...(border ? { border: `1px solid ${theme.palette.divider}` } : {}),
          borderRadius: 2,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {title ? (
        <CardHeader
          sx={{ p: 2.5 }}
          title={title}
          subheader={subheader}
          action={secondary}
          slotProps={{ title: { variant: "subtitle1" } }}
        />
      ) : null}
      {title && divider ? <Divider /> : null}
      {content ? <CardContent sx={contentSx}>{children}</CardContent> : children}
    </Card>
  );
}
